import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { TranslocoModule } from '@ngneat/transloco';
import { PostImageCropperComponent } from 'src/app/module/social-feed/post-image-cropper/post-image-cropper.component';
import {
  CropTransform, cropImageToFile, defaultCropTransform,
} from 'src/app/module/social-feed/post-image-cropper/image-crop.util';

/**
 * Diálogo para ajustar o recorte da FOTO DE PERFIL antes de subir (estilo Instagram).
 * Reaproveita o `app-post-image-cropper` (arrastar/zoom/pinça) com proporção fixa 1:1;
 * uma máscara circular guia o enquadramento (o avatar é exibido redondo). Ao confirmar,
 * gera um novo File quadrado recortado (via `cropImageToFile`) e o emite em `cropped`.
 *
 * Uso:
 *   <app-avatar-cropper [(visible)]="show" [file]="file" (cropped)="upload($event)"></app-avatar-cropper>
 */
@Component({
  selector: 'app-avatar-cropper',
  standalone: true,
  imports: [CommonModule, DialogModule, TranslocoModule, PostImageCropperComponent],
  template: `
    <p-dialog
      [visible]="visible"
      (visibleChange)="onDialogVisible($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="false"
      [closeOnEscape]="!busy"
      [header]="'socialFeed.adjustPhoto' | transloco"
      [style]="{ width: 'min(440px, 95vw)' }"
      styleClass="avatar-cropper-dialog"
    >
      <div class="avc-stage" *ngIf="src">
        <app-post-image-cropper [src]="src" [aspect]="1" [(transform)]="transform"></app-post-image-cropper>
        <!-- Guia circular (só visual): escurece fora do círculo do avatar. -->
        <div class="avc-circle"></div>
      </div>
      <p class="avc-hint">{{ 'socialFeed.cropHint' | transloco }}</p>

      <ng-template pTemplate="footer">
        <button type="button" class="avc-btn avc-btn--ghost" (click)="cancel()" [disabled]="busy">
          {{ 'socialFeed.cancel' | transloco }}
        </button>
        <button type="button" class="avc-btn avc-btn--primary" (click)="confirm()" [disabled]="busy || !src">
          {{ (busy ? 'socialFeed.saving' : 'socialFeed.save') | transloco }}
        </button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .avc-stage {
      position: relative;
      width: 100%;
      aspect-ratio: 1 / 1;
      background: #000;
      border-radius: 8px;
      overflow: hidden;
    }
    .avc-stage app-post-image-cropper { position: absolute; inset: 0; }
    .avc-circle {
      position: absolute;
      inset: 0;
      pointer-events: none;
      border-radius: 50%;
      box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.45);
    }
    .avc-hint {
      margin: 10px 2px 0;
      font-size: 0.8rem;
      color: var(--gray10-color, #9aa0a6);
      text-align: center;
    }
    .avc-btn {
      border: none;
      border-radius: 8px;
      padding: 9px 18px;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background 0.2s, opacity 0.2s;
    }
    .avc-btn:disabled { opacity: 0.6; cursor: default; }
    .avc-btn--ghost {
      background: transparent;
      color: var(--ftds-text, #fff);
      border: 1px solid rgba(255, 255, 255, 0.25);
      margin-right: 8px;
    }
    .avc-btn--ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
    .avc-btn--primary {
      background: var(--ftds-accent, #d81f1f);
      color: #fff;
    }
    .avc-btn--primary:hover:not(:disabled) { filter: brightness(1.08); }
  `],
})
export class AvatarCropperComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() file: File | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() cropped = new EventEmitter<File>();
  @Output() cancelled = new EventEmitter<void>();

  src: string | null = null;
  transform: CropTransform = defaultCropTransform();
  busy = false;

  ngOnChanges(ch: SimpleChanges): void {
    // Cada nova foto recomeça o recorte no centro, sem zoom.
    if (ch['file']) {
      this.revoke();
      this.transform = defaultCropTransform();
      this.src = this.file ? URL.createObjectURL(this.file) : null;
    }
  }

  ngOnDestroy(): void {
    this.revoke();
  }

  /** Fechar pelo X, Esc ou máscara equivale a cancelar. */
  onDialogVisible(v: boolean): void {
    if (!v && this.visible) {
      this.cancel();
    }
  }

  cancel(): void {
    this.close();
    this.cancelled.emit();
  }

  async confirm(): Promise<void> {
    if (!this.file || this.busy) return;
    this.busy = true;
    try {
      const out = await cropImageToFile(this.file, 1, this.transform);
      this.cropped.emit(out);
      this.close();
    } finally {
      this.busy = false;
    }
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.revoke();
  }

  private revoke(): void {
    if (this.src) {
      URL.revokeObjectURL(this.src);
      this.src = null;
    }
  }
}
